import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ClientService } from '../../core/services/client.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { CountryService } from '../../core/services/country.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PaymentsService } from '../../core/services/payments.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { ChangeData } from 'untold-ngx-intl-tel-input';
import { ToolsService } from '../../core/services/tools.service';
import { format, parse, parseISO } from 'date-fns';
import { MerchantsService } from '../../core/services/merchants.service';
import { IntlService } from '../../core/services/intl.service';
import { StoresService } from '../../core/services/stores.service';
import { ActivatedRoute } from '@angular/router';

declare var Stripe: stripe.StripeStatic;
declare const fbq;

@Component({
  selector: 'app-client-confirm',
  templateUrl: './client-confirm.component.html',
  styleUrls: ['./client-confirm.component.scss'],
})
export class ClientConfirmComponent implements OnInit, OnDestroy {
  @Input() merchant;
  @Input() store;
  @Input() services;
  @Input() providers;
  @Input() googleReserveToken: string;
  @Input() day;
  @Input() time;
  @Input() mandatoryEmailOnBooking = true;
  @Input() payOnlyPage = false;
  @Input() inputBooking;
  @Output() saved = new EventEmitter();

  public createBookingSubscription: Subscription;

  public form: UntypedFormGroup;

  public errorMsg;
  public loading = true;
  public submitAttempted = false;

  public stripe;
  public card;
  public stripePaymentIntentResponse;
  public stripeValid = false;
  public elements;
  public bookingCreated;
  public paymentsEnabled = false;
  public totalPriceToBePaid = 0;
  public totalPriceOriginal = 0;
  public paymentRequest;
  public applePayEnabled = false;
  public googlePayEnabled = false;
  public activatedDepositPayment = false;

  language: any;
  public merchantCustomFields: any;
  public onlyOneServiceWithoutPrice = false;
  mbrefTime: string;

  public genderOptions = [];

  constructor(
    private clientService: ClientService,
    private fb: UntypedFormBuilder,
    private countryService: CountryService,
    private paymentsService: PaymentsService,
    private translateService: TranslateService,
    private analytics: AnalyticsService,
    private toolsService: ToolsService,
    private merchantService: MerchantsService,
    private storesService: StoresService,
    private intlService: IntlService,
    private activatedRoute: ActivatedRoute
  ) {}

  get customFieldsControls()  {
    return this.form.get('customFields') as UntypedFormArray;
  }

  ngOnInit() {
    this.language = this.intlService.getClientLanguage();

    this.genderOptions = [
      { value: 'male', label: this.translateService.instant('GENERAL.gender.male')},
      { value: 'female', label: this.translateService.instant('GENERAL.gender.female') },
      { value: 'other', label: this.translateService.instant('GENERAL.gender.other') },
    ];

    this.mbrefTime = `10m`;

    if (this.store.cancelMbrefAfterMinutes) {
      if (this.store.cancelMbrefAfterMinutes > 60) {
        this.mbrefTime = `${Math.floor(this.store.cancelMbrefAfterMinutes / 60)}h`;
      } else {
        this.mbrefTime = `${this.store.cancelMbrefAfterMinutes}m`;
      }
    }

    this.totalPriceToBePaid = this.services.reduce((t, s) => {
      return Math.round(100 * (t + s.price)) / 100
    }, 0);
    this.totalPriceOriginal = this.totalPriceToBePaid;
    // deposit settings
    if (this.store.activeDepositPayments) {
      if (this.store.depositPaymentsValue && this.store.depositPaymentsType === 'percentage') {
        this.totalPriceToBePaid = Math.round(100 * (this.totalPriceToBePaid * (this.store.depositPaymentsValue/100))) / 100;
        this.activatedDepositPayment = true;
      }
      if (this.store.depositPaymentsValue && this.store.depositPaymentsType === 'fixed') {
        const depositValue = this.store.depositPaymentsValue * this.services.length;
        this.totalPriceToBePaid = depositValue < this.totalPriceOriginal ? depositValue : this.totalPriceOriginal;
        this.activatedDepositPayment = true;
      }
    }

    if (this.totalPriceToBePaid > 0 &&
      this.storesService.isPaymentsActive(this.store)
      ) {
      this.paymentsEnabled = true;
      this.setupStripe();
    } else {
      this.paymentsEnabled = false;
    }

    this.onlyOneServiceWithoutPrice = this.services.length === 1 && this.services.filter((x) => x.price === null || x.price === undefined || x.price === 0).length > 0;

    let localClientData;
    try {
      localClientData = localStorage?.getItem('book:client:data');
    } catch (error) {}
    if (localClientData) {
      this.getCustomFields(JSON.parse(localClientData));
    } else {
      this.getCustomFields();
    }
  }

  formatTime(time) {
    return format(parse(time, 'HH:mm', new Date()), this.intlService.getTimeFormat());
  }

  setupStripe() {
    if (!this.store.stripePaymentsEnabled) {
      return;
    }
    this.stripe = Stripe(environment.stripe.publishableKey);

    this.paymentsService
      .getStripeCustomerPaymentIntent(
        this.store.id,
        this.services?.map((i) => i.id)
      )
      .subscribe((data) => {
        this.stripePaymentIntentResponse = data;

        const elements = this.stripe.elements();
        const style = {
          base: {
            color: '#6772e5',
          },
        };

        this.card = elements.create('card', { style: style });
        this.card.mount('#card-element');

        this.card.on('change', ({ error }) => {
          let displayError = document.getElementById('card-errors');
          if (error) {
            this.stripeValid = false;
            displayError.textContent = error.message;
            this.analytics.trackEvent('setupStripe', 'payments', error.message);
          } else {
            this.stripeValid = true;
            displayError.textContent = '';
          }
        });
      });

    this.setupApplePayAndGooglePay();
  }

  setupApplePayAndGooglePay() {
    const merchantcountryCode = this.countryService.getCountryCode(this.store.country);

    this.paymentRequest = this.stripe.paymentRequest({
      country: merchantcountryCode?.toUpperCase() || 'PT',
      currency: this.store.currency.toLowerCase(),
      total: {
        label: this.store.name,
        amount:  Math.round(this.totalPriceToBePaid * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      onBehalfOf: this.store.connectAccountId
    });

    const elements = this.stripe.elements();
    const prButton = elements.create('paymentRequestButton', {
      paymentRequest: this.paymentRequest,
    });

    (async () => {
      // Check the availability of the Payment Request API first.
      const result = await this.paymentRequest.canMakePayment();
      if (result) {
        this.applePayEnabled = result.applePay;
        this.googlePayEnabled = result.googlePay;
        if (this.applePayEnabled) {
          this.form.get('paymentMethod').patchValue('applepay');
        }
        if (this.googlePayEnabled) {
          this.form.get('paymentMethod').patchValue('googlepay');
        }
        prButton.mount('#payment-request-button');
        document.getElementById('payment-request-button').attributes['disabled'] = true;
      } else {
        const paymentRequestButton = document.getElementById('payment-request-button');
        if (paymentRequestButton) {
          paymentRequestButton.style.display = 'none';
        }
      }
    })();

    this.paymentRequest.on('paymentmethod', async (ev) => {
      // Confirm the PaymentIntent without handling potential next actions (yet).
      this.createBooking(ev);
    });

    this.paymentRequest.on('cancel', async (ev) => {
      // this.paymentFailed(null);
      this.analytics.trackEvent('onCancel', 'payments', 'cancel');

      this.loading = false;
    });
  }

  async appleGooglePayEvent(ev) {
    const { paymentIntent, error: confirmError } = await this.stripe.confirmCardPayment(
      this.stripePaymentIntentResponse.clientSecret,
      { payment_method: ev.paymentMethod.id },
      { handleActions: false }
    );

    if (confirmError) {
      // Report to the browser that the payment failed, prompting it to
      // re-show the payment interface, or show an error message and close
      // the payment interface.
      ev.complete('fail');
      this.analytics.trackEvent('confirmError', 'payments', 'error');
      this.paymentFailed('Error paying, please try again.');
    } else {
      // Report to the browser that the confirmation was successful, prompting
      // it to close the browser payment method collection interface.
      ev.complete('success');
      // Check if the PaymentIntent requires any actions and if so let Stripe.js
      // handle the flow. If using an API version older than "2019-02-11"
      // instead check for: `paymentIntent.status === "requires_source_action"`.

      if (paymentIntent.status === 'requires_action') {
        // Let Stripe.js handle the rest of the payment flow.
        const { error } = await this.stripe.confirmCardPayment(this.stripePaymentIntentResponse.clientSecret);
        if (error) {
          this.paymentFailed('Error paying, please try again.');
          this.analytics.trackEvent('requiresAction', 'payments', 'error');
          // The payment failed -- ask your customer for a new payment method.
        } else {
          // The payment has succeeded.
          this.paymentSucceeded();
        }
      } else {
        // The payment has succeeded.
        this.paymentSucceeded();
      }
    }
  }
  paymentFailed(msg) {
    this.analytics.trackEvent('paymentFailed', 'payments', msg);
    this.errorMsg = msg;
    if (!this.payOnlyPage) {
      this.clientService.cancelBookingPayment(this.bookingCreated.id, this.bookingCreated.bookingCode).subscribe(async (res) => {});
    }
    this.loading = false;
  }
  paymentSucceeded() {
    if (this.merchant.bookingCompleteRedirectUrl) {
      window.location.href = this.merchant.bookingCompleteRedirectUrl;
      return;
    }
    this.saved.emit(this.bookingCreated);
    this.loading = false;
  }

  ngOnDestroy() {
    if (this.createBookingSubscription) {
      this.createBookingSubscription.unsubscribe();
    }
  }

  validatePtNumber(c: UntypedFormControl) {
    return c?.value?.e164Number?.includes('+351')
      ? null
      : {
          ptNumber: {
            valid: false,
          },
        };
  }

  getCustomFields(data = null) {
    this.merchantService.getMerchantCustomFields(this.merchant.slug).subscribe((res) => {
      this.generateForm(data, res?.filter(v => v.showOnBookingPage));
    }, (err) => {
      this.generateForm(data);
    });
  }

  generateForm(data = null, merchantCustomFields = null) {
    if (this.payOnlyPage) {
      return this.generateFormForPayOnlyPage();
    }

    const emailValidators = [Validators.email];
    if (this.mandatoryEmailOnBooking) {
      emailValidators.push(Validators.required);
    }

    const phoneValidators = [Validators.required];

    this.merchantCustomFields = merchantCustomFields;
    const customFields = this.fb.array([]);
    if (merchantCustomFields?.length) {
      merchantCustomFields.forEach((field) => {
        // get value from data where key of object in customFields array is equal to field.id
        let value = data?.customFields?.find(item => item?.hasOwnProperty('_' + field.id))?.['_' + field.id] || '';
        // if date convert to date
        if (field.type === 'date' && value) {
          value = parseISO(value);
        }
        // if dropdown and value is not an option in dropdownValues set value to ''
        if (field.type === 'dropdown' && value && !field.dropdownValues?.find(v => v === value)) {
          value = '';
        }
        const control = this.fb.group({
          ['_' + field.id]: [value, field.mandatory ? [Validators.required] : []],
        });
        customFields.push(control);
      });
    }
    // check if it's a date
    let dob = null;
    if (data?.dob) {
      dob = parseISO(data.dob);
      if (isNaN(dob.getTime())) {
        dob = null;
      }
    }


    this.form = this.fb.group({
      name: [(data && data.name) || '', [Validators.required]],
      phoneNumber: [(data && data.phoneNumber) || '', phoneValidators],
      email: [(data && data.email) || '', emailValidators],
      vatNumber: [(data && data.vatNumber) || '', []],
      gender: [(data && data.gender && this.genderOptions.find(v => v.value === data.gender)) || '', this.merchant.showGenderOnBookingPage ? [Validators.required] : []],
      dob: [dob || '', []],
      notes: ['', [Validators.maxLength(255)]],
      terms: [false, [Validators.requiredTrue]],
      marketing: [false, []],
      customFields: customFields
    });

    if (this.merchant.corporate) {
      this.form.addControl('termsCorporate', new UntypedFormControl(false, Validators.requiredTrue));
    }

    this.form.addControl('paymentMethod', new UntypedFormControl(this.defaultPaymentMethod(), Validators.required));
    this.setPaymentMethodValidator(this.defaultPaymentMethod(), phoneValidators);

    this.form.get('paymentMethod').valueChanges.subscribe((res) => {
      this.analytics.trackEvent('changePaymentMethod', 'payments', res);
      this.setPaymentMethodValidator(res, phoneValidators);
    });

    this.loading = false;
  }

  generateFormForPayOnlyPage() {
    const phoneValidators = [];

    this.form = this.fb.group({
      name: ['', []],
      phoneNumber: ['', []], // get from booking
      email: ['', []],
      vatNumber: [ '', []], // get from booking
      gender: ['', []],
      dob: ['', []],
      notes: ['', []],
      terms: [false, [Validators.requiredTrue]],
      marketing: [false, []],
      customFields: this.fb.array([]),
    });

    if (this.merchant.corporate) {
      this.form.addControl('termsCorporate', new UntypedFormControl(false, Validators.requiredTrue));
    }

    this.form.addControl('paymentMethod', new UntypedFormControl(this.defaultPaymentMethod(), Validators.required));
    this.setPaymentMethodValidator(this.defaultPaymentMethod(), phoneValidators);

    this.form.get('paymentMethod').valueChanges.subscribe((res) => {
      this.analytics.trackEvent('changePaymentMethod', 'payments', res);
      this.setPaymentMethodValidator(res, phoneValidators);
    });

    this.loading = false;
  }

  setPaymentMethodValidator(res, phoneValidators) {
    if (res === 'mbway') {
      this.form.get('phoneNumber').setValidators([Validators.required, this.validatePtNumber]);
    } else {
      this.form.get('phoneNumber').setValidators(phoneValidators);
    }
    this.form.get('phoneNumber').updateValueAndValidity();
  }

  parseFieldToPhoneNumber(value: ChangeData): string {
    return value?.e164Number ?? null;
  }

  defaultPaymentMethod() {
    if (!this.paymentsEnabled) {
      return 'money';
    }
    if (this.applePayEnabled) {
      return 'applepay';
    }
    if (this.googlePayEnabled) {
      return 'googlepay';
    }

    if (this.store.activeMBWay) {
      return 'mbway';
    }

    if (this.store.mbrefPaymentsEnabled) {
      return 'mbref';
    }

    return this.store.stripePaymentsEnabled ? 'ccard' : 'money';
  }

  getOrderTotal() {
    return this.services.reduce((a, b) => a + b.price, 0);
  }

  confirmBooking() {
    this.submitAttempted = true;
    this.errorMsg = null;

    if (!this.form.valid || (this.form.get('paymentMethod')?.value === 'ccard' && !this.stripeValid)) {
      return false;
    }

    this.loading = true;

    if (['applepay', 'googlepay'].includes(this.form.get('paymentMethod')?.value)) {
      this.analytics.trackEvent('showPaymentRequest', 'payments', this.form.get('paymentMethod')?.value);
      this.paymentRequest.show();
    } else {
      this.createBooking();
    }
  }

  payBooking(ev = null) {
    const payload: any = {
      phoneNumber: this.parseFieldToPhoneNumber(this.form.get('phoneNumber').value),
      vatNumber: this.form.get('vatNumber').value,
      unsubscribed: !!!this.form.get('marketing').value,
    };

    payload['paymentMethod'] = this.form.get('paymentMethod')?.value;
    if (['ccard', 'googlepay', 'applepay'].includes(payload['paymentMethod'])) {
      payload['paymentId'] = this.stripePaymentIntentResponse ? this.stripePaymentIntentResponse.id : null;
    }

    // this.facebookTrack('Purchase', payload.email, payload.phoneNumber);
    // this.analytics.trackClientEvent(this.merchant.gaPropertyId, 'payment', this.totalPriceOriginal, this.merchant.currency);

    this.clientService.createBookingPayment(this.inputBooking.id, this.inputBooking.bookingCode, payload).subscribe(
      (res) => {
        this.bookingCreated = res;
        this.checkCardPayments(ev);
      },
      (err) => {
        this.paymentFailed(err.errMsg);
      }
    );
  }

  createBooking(ev = null) {
    if (this.payOnlyPage) {
      return this.payBooking(ev);
    }

    const email = this.form.get('email').value === '' ? null : this.form.get('email').value;

    const fullName = this.form.get('name').value?.split(' ');
    const firstName = fullName[0];
    let lastName = '';
    if (fullName.length > 1) {
      lastName = fullName.slice(1).join(' ');
    }

    const sourcePayload = this.toolsService.getMerchantClientOriginPayload();

    const payload: any = {
      dateInit: this.day.day + ' ' + this.time.start,
      services: this.services?.map((i) => i.id),
      providers: this.providers?.map((i) => i.id),
      notes: this.form.get('notes').value?.trim(),
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
      email: email?.trim(),
      phoneNumber: this.parseFieldToPhoneNumber(this.form.get('phoneNumber').value),
      language: this.translateService.currentLang,
      unsubscribed: !!!this.form.get('marketing').value,
      source: sourcePayload
    };

    if (this.merchant.showVatNumberOnBookingPage) {
      payload.vatNumber = this.form.get('vatNumber').value;
    }

    if (this.merchant.showDOBOnBookingPage) {
      payload.dob = this.form.get('dob').value ? format(this.form.get('dob').value, 'yyyy-MM-dd') : null;
    }

    if (this.merchant.showGenderOnBookingPage) {
      payload.gender = this.form.get('gender').value ? this.form.get('gender').value.value : null;
    }

    if (this.merchantCustomFields?.length) {
      payload.customFields = {};
      this.form.get('customFields').value.forEach((field, index) => {
        let id = Object.keys(field)[0];
        let value: string = field[id];
        if (value && (value as any) instanceof Date) {
          value = format((value as any), 'yyyy-MM-dd');
        }
        id = id.replace('_', '');
        payload.customFields[id] = value;
      });
    }

    if (this.googleReserveToken) {
      payload['googleReserveToken'] = this.googleReserveToken;
    }

    this.facebookTrack('Schedule', payload.email, payload.phoneNumber);
    this.analytics.trackClientEvent(this.merchant.gaPropertyId, 'conversion', this.totalPriceOriginal, this.merchant.currency);

    if (this.form.get('paymentMethod')?.value) {
      payload['paymentMethod'] = this.form.get('paymentMethod')?.value;
      if (['ccard', 'googlepay', 'applepay'].includes(payload['paymentMethod'])) {
        payload['paymentId'] = this.stripePaymentIntentResponse ? this.stripePaymentIntentResponse.id : null;
      }

      this.facebookTrack('Purchase', payload.email, payload.phoneNumber);
      this.analytics.trackClientEvent(this.merchant.gaPropertyId, 'payment', this.totalPriceOriginal, this.merchant.currency);
    }

    this.createBookingSubscription = this.clientService.createBooking(this.store.id, payload).subscribe(
      async (res) => {
        try {
          localStorage?.setItem('book:client:data', JSON.stringify(this.form.value));
        } catch (error) {}
        this.bookingCreated = res;
        await this.checkCardPayments(ev);
      },
      (err) => {
        this.analytics.trackEvent('errorCreateBooking', 'payments', err.errMsg);
        this.errorMsg = err.errMsg;
        this.loading = false;
      }
    );
  }

  async checkCardPayments(ev = null) {
    if (['googlepay', 'applepay'].includes(this.form.get('paymentMethod').value)) {
      this.appleGooglePayEvent(ev);
    } else if (this.form.get('paymentMethod').value === 'ccard') {
      const result = await this.stripe.confirmCardPayment(this.stripePaymentIntentResponse.clientSecret, {
        payment_method: {
          card: this.card,
          billing_details: {
            name: this.form.get('name').value,
          },
        },
      });

      if (result.error) {
        //  delete booking and return
        this.paymentFailed(result.error.message);
      } else {
        // The payment has been processed!
        if (result.paymentIntent.status === 'succeeded') {
          this.paymentSucceeded();
        }
      }
    } else {
      this.paymentSucceeded();
    }
  }

  async facebookTrack(event: 'Schedule' | 'Purchase', email, phoneNumber) {
    if (this.merchant?.facebookConversionToken) {
      const hashedEmail = await this.toolsService.sha256Hash(email);
      const hashedPhone = await this.toolsService.sha256Hash(phoneNumber);
      const metaParams = this.activatedRoute.snapshot.queryParams;
      this.clientService
        .sendFacebookConversionApiEvent(this.merchant.facebookPixel, this.merchant.facebookConversionToken, {
          data: [
            {
              event_name: event,
              event_time: Math.floor(new Date().getTime() / 1000),
              action_source: 'website',

              user_data: {
                em: [hashedEmail],
                fbc: this.toolsService.getCookie('_fbc') || null,
                fbp: this.toolsService.getCookie('_fbp') || null
              },
              custom_data: {
                currency: this.merchant.currency,
                value: this.totalPriceToBePaid,
              },
            },
          ],
        })
        .subscribe();
    } else if (this.merchant?.facebookPixel) {
      if (typeof fbq === 'function') {
        fbq('trackSingle', this.merchant.facebookPixel, event, {
          value: this.totalPriceToBePaid,
          currency: this.merchant.currency,
        });
      }
    }
  }
}
